import React, { useState, useEffect } from 'react'
import { IoTrendingDown, IoChevronDown, IoChevronUp } from 'react-icons/io5'
import { getDroppingOdds, type DroppingOddsItem } from '../api'
import { useOdds } from '../context/OddsContext'

interface DroppingOddsSectionProps {
  leagueIds?: number[]
  fixtureId?: number
  limit?: number
  collapsedByDefault?: boolean
}

function getSeverityClass(dropPct: number): string {
  if (dropPct >= 10) return 'high'
  if (dropPct >= 5) return 'med'
  return 'low'
}

function DroppingOddsRow({ item }: { item: DroppingOddsItem }) {
  const { formatOdds } = useOdds()
  const severity = getSeverityClass(item.drop_pct)

  return (
    <div className={`dropping-odds-row ${severity}`}>
      <div className="dropping-odds-main">
        <div className="dropping-odds-drop">
          <IoTrendingDown size={14} />
          <span className="drop-pct">{item.drop_pct.toFixed(0)}%</span>
        </div>
        <div className="dropping-odds-info">
          <span className="dropping-selection">{item.selection_label}</span>
          <span className="dropping-fixture">{item.fixture_label}</span>
        </div>
      </div>
      <div className="dropping-odds-values">
        <span className="dropping-odds-old">{formatOdds(item.previous_odds)}</span>
        <span className="dropping-odds-arrow">&rarr;</span>
        <span className="dropping-odds-new">{formatOdds(item.current_odds)}</span>
      </div>
      <div className="dropping-odds-bookmaker">
        {item.bookmaker_name}
      </div>
    </div>
  )
}

export function DroppingOddsSection({
  leagueIds,
  fixtureId,
  limit = 5,
  collapsedByDefault = true,
}: DroppingOddsSectionProps) {
  const [items, setItems] = useState<DroppingOddsItem[]>([])
  const [loading, setLoading] = useState(true)
  const [isCollapsed, setIsCollapsed] = useState(collapsedByDefault)

  useEffect(() => {
    setLoading(true)
    getDroppingOdds({
      hours_back: 6,
      min_drop_pct: 5,
      league_ids: leagueIds,
      fixture_id: fixtureId,
      limit,
    })
      .then((response) => {
        setItems(response.items)
      })
      .catch((err) => {
        console.error('Failed to fetch dropping odds:', err)
      })
      .finally(() => setLoading(false))
  }, [leagueIds, fixtureId, limit])

  // Don't render if no items
  if (!loading && items.length === 0) return null

  return (
    <div className="dropping-odds-section">
      <button
        className="dropping-odds-header"
        onClick={() => setIsCollapsed(!isCollapsed)}
      >
        <div className="dropping-odds-title">
          <IoTrendingDown size={18} className="dropping-icon" />
          <span>Dropping Odds</span>
          {items.length > 0 && (
            <span className="dropping-count">{items.length}</span>
          )}
        </div>
        {isCollapsed ? <IoChevronDown size={18} /> : <IoChevronUp size={18} />}
      </button>

      {!isCollapsed && (
        <div className="dropping-odds-content">
          {loading ? (
            <div className="dropping-odds-loading">Loading...</div>
          ) : (
            <div className="dropping-odds-list">
              {items.map((item, idx) => (
                <DroppingOddsRow
                  key={`${item.fixture_id}-${item.market_slug}-${item.outcome}-${idx}`}
                  item={item}
                />
              ))}
            </div>
          )}
          <div className="dropping-odds-footer">
            Odds that dropped 5%+ in the last 6 hours
          </div>
        </div>
      )}
    </div>
  )
}
